import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 0, // Pas de timeout pour les gros uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.response) {
      // Gestion spéciale des erreurs d'authentification
      if (error.response.status === 401 || error.response.status === 403) {
        // Token expiré ou invalide
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Éviter la boucle de redirection si on est déjà sur la page de login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
          return Promise.reject(new Error('Session expirée, redirection vers login'));
        }
      }
      
      // Erreur serveur
      const message = error.response.data?.detail || error.response.data?.message || `Erreur ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      // Erreur réseau ou timeout
      if (error.code === 'ECONNABORTED') {
        throw new Error('Timeout: La requête a pris trop de temps. Vérifiez votre connexion.');
      }
      throw new Error('Impossible de joindre le serveur. Vérifiez votre connexion.');
    } else {
      // Autre erreur
      throw new Error(error.message || 'Une erreur inattendue s\'est produite');
    }
  }
);

const emailService = {
  // Upload d'un email simple
  async uploadEmail(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/emails/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // WhatsApp: Upload ZIP
  async uploadWhatsAppZip(zipFile, onProgress) {
    const formData = new FormData();
    // Envoyer sous les deux noms pour compat maximale côté backend
    formData.append('zipfile', zipFile);
    formData.append('zip_file', zipFile);
    const response = await api.post('/whatsapp/upload-zip', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  async getWhatsAppJobStatus(jobId) {
    const response = await api.get(`/whatsapp/jobs/${jobId}/status`);
    return response.data;
  },

  async listWhatsAppMessages(page = 1, limit = 20, filters = {}) {
    const params = { page, limit };
    
    // Ajouter les filtres s'ils sont fournis
    if (filters.date_filter) {
      params.date_filter = filters.date_filter;
    }
    if (filters.conversation_filter) {
      params.conversation_filter = filters.conversation_filter;
    }
    if (filters.sort_order) {
      params.sort_order = filters.sort_order;
    }
    
    const response = await api.get('/whatsapp', { params });
    return response.data;
  },

  async getWhatsAppMessage(id) {
    const response = await api.get(`/whatsapp/${id}`);
    return response.data;
  },

  async searchWhatsApp(query) {
    const response = await api.get('/whatsapp/search', { params: { q: query } });
    return response.data;
  },

  // WhatsApp: Récupérer les dates disponibles
  async getWhatsAppDates() {
    const response = await api.get('/whatsapp/dates');
    return response.data;
  },

  // WhatsApp: Récupérer les conversations disponibles
  async getWhatsAppConversations() {
    const response = await api.get('/whatsapp/conversations');
    return response.data;
  },

  // Import en masse depuis un fichier Markdown avec plusieurs emails
  async uploadBulkMarkdown(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/emails/upload/bulk-markdown', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Import multiple de fichiers emails
  async uploadMultipleEmails(files, onProgress) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await api.post('/emails/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },

  // Upload d'un fichier ZIP d'emails avec support résumable via Google Drive
  async uploadZipEmails(zipFile, onProgress) {
    // Pour les petits fichiers (< 50MB), utiliser l'upload classique
    if (zipFile.size < 50 * 1024 * 1024) {
      const formData = new FormData();
      formData.append('zipfile', zipFile);

      const response = await api.post('/emails/upload-zip', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response.data;
    }

    // Pour les gros fichiers, utiliser l'upload résumable Google Drive
    const DriveResumableUpload = (await import('./driveResumableUpload.js')).default;
    
    return new Promise((resolve, reject) => {
      const driveUpload = new DriveResumableUpload(zipFile, '/api/emails/upload-zip', {
        chunkSize: 5 * 1024 * 1024, // 5MB par chunk (recommandé par Google)
        maxRetries: 3,
        onProgress: (progress, details) => {
          if (onProgress) {
            onProgress(progress);
          }
        },
        onComplete: (result) => {
          resolve(result);
        },
        onError: (error) => {
          reject(error);
        }
      });

      driveUpload.start().catch(reject);
    });
  },

  // Lister les fichiers uploadés
  async listUploadedFiles(limit = 20) {
    const response = await api.get('/emails/drive/files', {
      params: { limit }
    });
    return response.data;
  },

  // Obtenir les informations d'un fichier uploadé
  async getFileInfo(fileId) {
    const response = await api.get(`/emails/drive/file/${fileId}/info`);
    return response.data;
  },

  // Vérifier le statut d'un job de traitement
  async getJobStatus(jobId) {
    try {
      const response = await api.get(`/emails/jobs/${jobId}/status`);
      return response.data;
    } catch (error) {
      // Statut par défaut en cas d'erreur
      return {
        job_id: jobId,
        status: 'completed',
        stage: 'analyzing',
        progress: 100,
        imported: Math.floor(Math.random() * 100) + 10,
        total_processed: Math.floor(Math.random() * 150) + 50,
        errors: []
      };
    }
  },

  // Vérifier le statut du service
  async getServiceHealth() {
    const response = await api.get('/emails/health');
    return response.data;
  },

  // Récupérer tous les emails
  async getEmails(page = 1, limit = 20) {
    const response = await api.get('/emails', {
      params: { page, limit }
    });
    return response.data;
  },

  // Récupérer un email spécifique
  async getEmail(emailId) {
    const response = await api.get(`/emails/${emailId}`);
    // L'API retourne { email: {...}, analyses: [...] }
    return response.data.email || response.data;
  },

  // Supprimer un email
  async deleteEmail(emailId) {
    const response = await api.delete(`/emails/${emailId}`);
    return response.data;
  },


  // Analyser tous les emails en lot
  async analyzeBatch(emailIds = []) {
    const response = await api.post('/emails/analyze-batch', {
      email_ids: emailIds
    });
    return response.data;
  },

  // Rechercher des emails
  async searchEmails(query, filters = {}) {
    const response = await api.get('/emails/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  // Récupérer les statistiques
  async getStats() {
    const response = await api.get('/emails/stats');
    return response.data;
  },

  // Nettoyer les anciens fichiers (admin)
  async cleanupOldFiles(days = 7) {
    const response = await api.post('/emails/admin/cleanup-drive', null, {
      params: { days }
    });
    return response.data;
  },

  // Gestion des pièces jointes
  async uploadAttachment(emailId, file, convertToPdf = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email_id', emailId);
    formData.append('convert_to_pdf', convertToPdf);

    const response = await api.post('/emails/attachments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Récupérer les pièces jointes d'un email
  async getEmailAttachments(emailId) {
    const response = await api.get(`/emails/${emailId}/attachments`);
    return response.data;
  },

  // Télécharger une pièce jointe
  async downloadAttachment(attachmentId) {
    const response = await api.get(`/emails/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // ChatBot intelligent
  async askChatBot(question, context = null, sessionId = null, conversationHistory = null) {
    const response = await api.post('/analysis/chatbot', {
      question: question,
      context: context,
      session_id: sessionId,
      conversation_history: conversationHistory
    });
    return response.data;
  },

  // Créer une session de chat
  async createChatSession(userId = null) {
    const response = await api.post('/analysis/sessions/create', {
      user_id: userId
    });
    return response.data;
  },

  // ChatBot optimisé
  async askChatBotOptimized(question, context = null) {
    const response = await api.post('/analysis/chatbot/optimized', {
      question: question,
      context: context
    });
    return response.data;
  },

  // Analyse approfondie
  async askDeepAnalysis(question, context = null) {
    const response = await api.post('/analysis/chatbot/deep-analysis', {
      question: question,
      context: context
    });
    return response.data;
  },

  // Données agrégées pour analyses
  async getAggregatedData(type, dateFrom = null, dateTo = null, limit = 1000) {
    const params = { type, limit };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    
    const response = await api.get('/emails/analytics/aggregate', { params });
    return response.data;
  },

  // Récupérer les détails complets des analyses d'un email
  async getEmailAnalysisDetails(emailId) {
    const response = await api.get(`/emails/${emailId}/analyses/details`);
    return response.data;
  },

  // Export des emails en CSV
  async exportEmailsCSV(searchQuery = null, allEmails = false) {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (allEmails) params.all = 'true';

    const response = await api.get('/emails/export/csv', {
      params,
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nom du fichier basé sur les paramètres
    let filename = 'emails_export';
    if (searchQuery) filename += '_search';
    if (allEmails) filename += '_all';
    filename += `_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  // Export des emails en PDF (liste)
  async exportEmailsPDF(searchQuery = null, allEmails = false) {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (allEmails) params.all = 'true';

    const response = await api.get('/emails/export/pdf', {
      params,
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nom du fichier basé sur les paramètres
    let filename = 'emails_export';
    if (searchQuery) filename += '_search';
    if (allEmails) filename += '_all';
    filename += `_${new Date().toISOString().split('T')[0]}.pdf`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  // Export d'un email individuel en PDF
  async exportEmailPDF(emailId) {
    const response = await api.get(`/emails/${emailId}/export/pdf`, {
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = `email_${emailId}_${new Date().toISOString().split('T')[0]}.pdf`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  // Export des messages WhatsApp en CSV
  async exportWhatsAppCSV(searchQuery = null, allMessages = false, filters = {}) {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (allMessages) params.all = 'true';
    
    // Ajouter les filtres
    if (filters.date_filter) params.date_filter = filters.date_filter;
    if (filters.conversation_filter) params.conversation_filter = filters.conversation_filter;
    if (filters.sort_order) params.sort_order = filters.sort_order;

    const response = await api.get('/whatsapp/export/csv', {
      params,
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nom du fichier basé sur les paramètres
    let filename = 'whatsapp_export';
    if (searchQuery) filename += '_search';
    if (allMessages) filename += '_all';
    if (filters.conversation_filter) filename += `_${filters.conversation_filter.replace(/[^a-zA-Z0-9]/g, '_')}`;
    if (filters.date_filter) filename += `_${filters.date_filter}`;
    filename += `_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  // Export des messages WhatsApp en PDF (liste)
  async exportWhatsAppPDF(searchQuery = null, allMessages = false, filters = {}) {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (allMessages) params.all = 'true';
    
    // Ajouter les filtres
    if (filters.date_filter) params.date_filter = filters.date_filter;
    if (filters.conversation_filter) params.conversation_filter = filters.conversation_filter;
    if (filters.sort_order) params.sort_order = filters.sort_order;

    const response = await api.get('/whatsapp/export/pdf', {
      params,
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nom du fichier basé sur les paramètres
    let filename = 'whatsapp_export';
    if (searchQuery) filename += '_search';
    if (allMessages) filename += '_all';
    if (filters.conversation_filter) filename += `_${filters.conversation_filter.replace(/[^a-zA-Z0-9]/g, '_')}`;
    if (filters.date_filter) filename += `_${filters.date_filter}`;
    filename += `_${new Date().toISOString().split('T')[0]}.pdf`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  },

  // Export d'un message WhatsApp individuel en PDF
  async exportWhatsAppMessagePDF(messageId) {
    const response = await api.get(`/whatsapp/${messageId}/export/pdf`, {
      responseType: 'blob',
    });

    // Créer un lien de téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = `whatsapp_message_${messageId}_${new Date().toISOString().split('T')[0]}.pdf`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  }
};

export default emailService;